import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'

import { forgetRun, personalProject } from '@shared/services/pictureService'
import { uploadProjectFile } from '@shared/services/projectFileService'
import {
  downloadLink,
  listVideos,
  startVideo,
  videoFileName,
  videoOptions,
  type VideoModel,
  type VideoRun,
} from '@shared/services/videoService'
import { WwLoaderFill } from '@shared/components/WwLoader'
import { useRemembered } from '../useRemembered'
import './videoPage.css'

/**
 * Video Generation, as a page (Huanrui, 2026-10-08)
 * -------------------------------------------------------------
 * First a basic version: a first frame, an optional last frame, a prompt, a
 * model and a length; the videos down the right, newest first, each with what
 * it was made from. Kling O3 at 4K, Kling v4 at 1080p or Seedance 2.5 at
 * 1080p, all through fal, always with sound (2026-10-27): the server's list
 * (workers/api/src/video.ts).
 *
 * A video takes minutes. The run carries on on the server with the tab
 * closed; the page asks every few seconds while one is being made.
 */

interface Frame {
  id: string
  url: string
  pending?: boolean
}

interface Choice {
  model: string
  seconds: number
  audio: boolean
}

const usd = (value: number) => `$${value.toFixed(2)}`

async function measure(file: File): Promise<{ width: number; height: number } | undefined> {
  try {
    const bitmap = await createImageBitmap(file)
    const size = { width: bitmap.width, height: bitmap.height }
    bitmap.close()
    return size
  } catch {
    return undefined
  }
}

export function VideoPage() {
  const [project, setProject] = useState('')
  const [models, setModels] = useState<VideoModel[]>([])
  const [runs, setRuns] = useState<VideoRun[]>([])
  const [error, setError] = useState('')
  const [first, setFirst] = useState<Frame | null>(null)
  const [last, setLast] = useState<Frame | null>(null)
  const [prompt, setPrompt] = useState('')
  const [starting, setStarting] = useState(false)
  const [choice, setChoice] = useRemembered<Choice>('ww.video.choice', { model: 'seedance-2.5', seconds: 5, audio: false })

  // The first link each video was given, kept while it is good (signed for
  // two hours; renewed after ninety minutes): every answer signs it afresh,
  // and a new link would reload a playing video.
  const links = useRef(new Map<string, { url: string; at: number }>())
  const steady = useCallback((list: VideoRun[]) => {
    const now = Date.now()
    for (const run of list) {
      if (!run.video) continue
      const kept = links.current.get(run.video.id)
      if (kept && now - kept.at < 90 * 60 * 1000) run.video.url = kept.url
      else links.current.set(run.video.id, { url: run.video.url, at: now })
    }
    return list
  }, [])

  const refresh = useCallback(
    async (id: string) => {
      try {
        setRuns(steady(await listVideos(id)))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The list could not be read')
      }
    },
    [steady],
  )

  useEffect(() => {
    void Promise.all([personalProject(), videoOptions()])
      .then(([id, found]) => {
        setProject(id)
        setModels(found)
        void refresh(id)
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'This page could not load'))
  }, [refresh])

  // Asked again every eight seconds while a video is being made.
  const making = runs.some((run) => run.status === 'queued' || run.status === 'running')
  useEffect(() => {
    if (!making || !project) return
    const timer = window.setInterval(() => void refresh(project), 8000)
    return () => window.clearInterval(timer)
  }, [making, project, refresh])

  const model = models.find((one) => one.key === choice.model) ?? models[0]
  const seconds = model ? Math.min(model.maxSeconds, Math.max(model.minSeconds, choice.seconds)) : choice.seconds
  // Always with sound (Huanrui, 2026-10-27): no longer a choice.
  const estimate = model ? seconds * model.perSecond.audio : 0

  const take = async (file: File, which: 'first' | 'last') => {
    if (!project || !/^image\/(png|jpe?g)$/.test(file.type)) {
      setError('A frame has to be a PNG or JPEG picture')
      return
    }
    setError('')
    const local = URL.createObjectURL(file)
    const set = which === 'first' ? setFirst : setLast
    set({ id: '', url: local, pending: true })
    try {
      const stored = await uploadProjectFile(project, file, await measure(file))
      set({ id: stored.id, url: local })
    } catch (err) {
      set(null)
      setError(err instanceof Error ? err.message : 'The picture could not be uploaded')
    }
  }

  const start = async () => {
    if (!project || !model || !first?.id || first.pending || last?.pending) return
    if (!prompt.trim()) {
      setError('Say what should happen in the video')
      return
    }
    setStarting(true)
    setError('')
    try {
      await startVideo({
        projectId: project,
        firstFrame: { store: 'project', id: first.id },
        lastFrame: last?.id ? { store: 'project', id: last.id } : undefined,
        model: model.key,
        seconds,
        audio: true,
        prompt: prompt.trim(),
      })
      await refresh(project)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The video did not start')
    } finally {
      setStarting(false)
    }
  }

  const labelOf = useMemo(() => new Map(models.map((one) => [one.key, one.label])), [models])

  return (
    <div className="vid">
      <section className="vid__make">
        <div className="vid__field">
          <span className="vid__label">Frames</span>
          <div className="vid__frames">
            <FrameTile label="First frame" frame={first} onFile={(file) => void take(file, 'first')} onClear={() => setFirst(null)} />
            <FrameTile
              label="Last frame"
              optional
              frame={last}
              onFile={(file) => void take(file, 'last')}
              onClear={() => setLast(null)}
            />
          </div>
        </div>

        <label className="vid__field">
          <span className="vid__label">What happens</span>
          <textarea
            className="vid__prompt"
            value={prompt}
            rows={5}
            onChange={(event) => setPrompt(event.target.value)}
          />
        </label>

        <div className="vid__field">
          <span className="vid__label">Model</span>
          <div className="vid__models">
            {models.map((one) => (
              <button
                key={one.key}
                type="button"
                className={`vid__model${one.key === model?.key ? ' vid__model--on' : ''}`}
                disabled={!one.ready}
                title={one.ready ? one.blurb : 'Its key has not been set yet'}
                onClick={() => setChoice({ ...choice, model: one.key, seconds: Math.min(one.maxSeconds, Math.max(one.minSeconds, choice.seconds)) })}
              >
                <strong>{one.label}</strong>
                <span>{one.ready ? one.blurb : 'Not switched on yet'}</span>
              </button>
            ))}
          </div>
        </div>

        {model ? (
          <div className="vid__field">
            <span className="vid__label">
              Length <em>{seconds} s</em>
            </span>
            <input
              type="range"
              className="vid__range"
              min={model.minSeconds}
              max={model.maxSeconds}
              step={1}
              value={seconds}
              onChange={(event) => setChoice({ ...choice, seconds: Number(event.target.value) })}
            />
            <div className="vid__range-ends">
              <span>{model.minSeconds} s</span>
              <span>{model.maxSeconds} s</span>
            </div>
          </div>
        ) : null}

        <span className="vid__spacer" />
        {error ? <p className="vid__error">{error}</p> : null}
        <button
          type="button"
          className="vid__go"
          disabled={!model?.ready || !first?.id || Boolean(first?.pending || last?.pending) || starting}
          onClick={() => void start()}
        >
          {starting ? 'Starting…' : 'Make the video'}
          {model ? (
            <small>
              {model.resolution.toUpperCase()} · {seconds} s · about {usd(estimate)}
            </small>
          ) : null}
        </button>
      </section>

      <section className="vid__list" aria-label="Videos">
        {runs.length === 0 ? <p className="vid__quiet">Your videos appear here.</p> : null}
        {runs.map((run) => (
          <article key={run.id} className="vid__run">
            <div className="vid__screen">
              {run.status === 'succeeded' && run.video ? (
                <video src={run.video.url} controls playsInline preload="metadata" />
              ) : run.status === 'failed' ? (
                <p className="vid__failed">{run.error || 'The video could not be made'}</p>
              ) : (
                <div className="vid__wait">
                  <WwLoaderFill label={run.status === 'queued' ? 'Waiting to start…' : 'Making the video… this takes a few minutes'} />
                </div>
              )}
            </div>
            <div className="vid__about">
              <div className="vid__frames-small">
                {run.frames.first ? <img src={run.frames.first} alt="First frame" /> : null}
                {run.frames.last ? <img src={run.frames.last} alt="Last frame" /> : null}
              </div>
              <p className="vid__said">{run.prompt}</p>
              <p className="vid__meta">
                {labelOf.get(run.model) ?? run.model} · {run.seconds} s{run.audio ? ' · sound' : ''}
                {run.credits ? ` · ${usd(run.credits / 1000)}` : run.estimateUsd ? ` · about ${usd(run.estimateUsd)}` : ''}
              </p>
              <div className="vid__acts">
                {run.video ? (
                  <a
                    className="vid__act"
                    href={downloadLink(run.video.url, videoFileName(run))}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download
                  </a>
                ) : null}
                {run.status === 'succeeded' || run.status === 'failed' ? (
                  <button
                    type="button"
                    className="vid__act"
                    onClick={() =>
                      void forgetRun(run.id).then(() => setRuns((now) => now.filter((one) => one.id !== run.id)))
                    }
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  )
}

function FrameTile({
  label,
  optional,
  frame,
  onFile,
  onClear,
}: {
  label: string
  optional?: boolean
  frame: Frame | null
  onFile: (file: File) => void
  onClear: () => void
}) {
  const input = useRef<HTMLInputElement | null>(null)
  const [over, setOver] = useState(false)
  const drop = (event: DragEvent) => {
    event.preventDefault()
    setOver(false)
    const file = event.dataTransfer.files?.[0]
    if (file) onFile(file)
  }
  return (
    <div
      className={`vid__frame${over ? ' vid__frame--over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      {frame ? (
        <>
          <img src={frame.url} alt={label} />
          {frame.pending ? <span className="vid__frame-note">Uploading…</span> : null}
          <button type="button" className="vid__frame-x" onClick={onClear} aria-label={`Remove the ${label.toLowerCase()}`}>
            ×
          </button>
        </>
      ) : (
        <button type="button" className="vid__frame-add" onClick={() => input.current?.click()}>
          <span className="vid__plus">+</span>
          <strong>{label}</strong>
          <em>{optional ? 'optional' : 'PNG or JPEG'}</em>
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) onFile(file)
          event.target.value = ''
        }}
      />
    </div>
  )
}
